import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSchedule, generateTimeSlots } from "@/lib/schedule";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderModal = ({
  isOpen,
  onClose,
  cart,
  language,
  getCartTotal,
  deliveryConfig,
  onOrderSuccess,
  cardPaymentsEnabled = false,
  initialDeliveryType = "delivery",
  restaurantOpen = true
}) => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth(); // Get logged-in user and update function
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    delivery_type: "delivery", // "delivery" or "pickup"
    delivery_address: "",
    payment_method: "cash",
    delivery_time: "", // New field for scheduled delivery
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [addressValidation, setAddressValidation] = useState(null);
  const [validatingAddress, setValidatingAddress] = useState(false);
  const schedule = useSchedule(isOpen);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (!isOpen || !user?.id) { setLoyalty(null); return; }
    axios.get(`${API}/loyalty/${user.id}`).then((res) => setLoyalty(res.data)).catch(() => setLoyalty(null));
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(prev => ({ ...prev, delivery_type: initialDeliveryType }));
  }, [isOpen, initialDeliveryType]);
  
  // Auto-fill form with user data when logged in
  useEffect(() => {
    if (user && isOpen) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.full_name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email,
        delivery_address: user.default_delivery_address || prev.delivery_address
      }));
    }
  }, [user, isOpen]);
  
  // Calculate delivery fee based on delivery type and subtotal
  const calculateDeliveryFee = () => {
    if (!deliveryConfig) return 0;
    if (formData.delivery_type === "pickup") return 0;
    
    const subtotal = getCartTotal();
    if (subtotal >= deliveryConfig.free_delivery_threshold) return 0;
    if (subtotal >= deliveryConfig.minimum_order_amount) return deliveryConfig.delivery_fee;
    
    return deliveryConfig.delivery_fee;
  };
  
  // Calculate pickup discount (10% for pickup orders)
  const calculatePickupDiscount = () => {
    if (!deliveryConfig) return 0;
    if (formData.delivery_type !== "pickup") return 0;
    
    const subtotal = getCartTotal();
    return subtotal * (deliveryConfig.pickup_discount_percent / 100);
  };
  
  // Loyalty: every 10th pizza free for registered users (most expensive pizzas in the order)
  const computeLoyalty = () => {
    if (!loyalty || !user?.id) return { free: 0, discount: 0 };
    const unitPrices = cart.filter(i => i.category === "pizza").flatMap(i => Array(i.quantity).fill(i.price));
    const before = loyalty.pizzas_bought;
    const every = loyalty.free_every;
    const free = Math.max(0, Math.min(unitPrices.length, Math.floor((before + unitPrices.length) / every) - Math.floor(before / every)));
    const discount = unitPrices.sort((a, b) => b - a).slice(0, free).reduce((a, b) => a + b, 0);
    return { free, discount };
  };
  const loyaltyInfo = computeLoyalty();
  const cartPizzas = cart.filter(i => i.category === "pizza").reduce((a, i) => a + i.quantity, 0);

  const deliveryFee = calculateDeliveryFee();
  const pickupDiscount = calculatePickupDiscount();
  const subtotal = getCartTotal();
  const totalWithDelivery = Math.max(0, subtotal + deliveryFee - pickupDiscount - loyaltyInfo.discount);
  
  // Check if minimum order requirement is met for delivery
  const isDeliveryBelowMinimum = deliveryConfig && formData.delivery_type === "delivery" && subtotal < deliveryConfig.minimum_order_amount;
  const amountNeeded = isDeliveryBelowMinimum ? (deliveryConfig.minimum_order_amount - subtotal).toFixed(2) : 0;
  
  // Check if orders are open (launch date check)
  const ordersNotYetAvailable = deliveryConfig && !deliveryConfig.orders_open;

  const translations = {
    title: {
      en: "Complete Your Order",
      ru: "Оформление заказа",
      lv: "Pabeigt pasūtījumu"
    },
    name: {
      en: "Full Name",
      ru: "Полное имя",
      lv: "Pilns vārds"
    },
    phone: {
      en: "Phone Number",
      ru: "Номер телефона",
      lv: "Tālruņa numurs"
    },
    email: {
      en: "Email",
      ru: "Email",
      lv: "E-pasts"
    },
    address: {
      en: "Delivery Address",
      ru: "Адрес доставки",
      lv: "Piegādes adrese"
    },
    loyaltyProgress: { en: "Pizza loyalty", ru: "Бонусные пиццы", lv: "Picu lojalitāte" },
    loyaltyNext: { en: "pizzas until a free one:", ru: "пицц до бесплатной:", lv: "picas līdz bezmaksas:" },
    loyaltyFreeNow: { en: "Free pizza in this order!", ru: "Бесплатная пицца в этом заказе!", lv: "Bezmaksas pica šajā pasūtījumā!" },
    loyaltyDiscount: { en: "Free pizza", ru: "Бесплатная пицца", lv: "Bezmaksas pica" },
    payment: {
      en: "Payment Method",
      ru: "Способ оплаты",
      lv: "Maksājuma veids"
    },
    cash: {
      en: "Cash on Delivery",
      ru: "Наличными при получении",
      lv: "Skaidra nauda piegādē"
    },
    card: {
      en: "Credit/Debit Card",
      ru: "Банковская карта",
      lv: "Bankas karte"
    },
    notes: {
      en: "Order Notes (Optional)",
      ru: "Примечания к заказу (необязательно)",
      lv: "Pasūtījuma piezīmes (nav obligāti)"
    },
    subtotal: {
      en: "Subtotal",
      ru: "Промежуточный итог",
      lv: "Starpsumma"
    },
    deliveryFee: {
      en: "Delivery Fee",
      ru: "Доставка",
      lv: "Piegādes maksa"
    },
    pickupDiscount: {
      en: "Pickup Discount (10%)",
      ru: "Скидка на самовывоз (10%)",
      lv: "Līdzņemšanas atlaide (10%)"
    },
    freeDelivery: {
      en: "Free Delivery!",
      ru: "Бесплатная доставка!",
      lv: "Bezmaksas piegāde!"
    },
    total: {
      en: "Total",
      ru: "Итого",
      lv: "Kopā"
    },
    placeOrder: {
      en: "Place Order",
      ru: "Оформить заказ",
      lv: "Veikt pasūtījumu"
    },
    cancel: {
      en: "Cancel",
      ru: "Отмена",
      lv: "Atcelt"
    },
    success: {
      en: "Order placed successfully!",
      ru: "Заказ успешно оформлен!",
      lv: "Pasūtījums veiksmīgi veikts!"
    },
    error: {
      en: "Failed to place order. Please try again.",
      ru: "Не удалось оформить заказ. Пожалуйста, попробуйте снова.",
      lv: "Neizdevās veikt pasūtījumu. Lūdzu, mēģiniet vēlreiz."
    },
    fillRequired: {
      en: "Please fill in all required fields",
      ru: "Пожалуйста, заполните все обязательные поля",
      lv: "Lūdzu, aizpildiet visus obligātos laukus"
    },
    paymentError: {
      en: "Payment processing failed. Please try again.",
      ru: "Ошибка обработки платежа. Пожалуйста, попробуйте снова.",
      lv: "Maksājuma apstrāde neizdevās. Lūdzu, mēģiniet vēlreiz."
    },
    deliveryTime: {
      en: "Delivery Time (Optional)",
      ru: "Время доставки (необязательно)",
      lv: "Piegādes laiks (nav obligāti)"
    },
    deliveryTimeRequired: {
      en: "Delivery Time",
      ru: "Время доставки",
      lv: "Piegādes laiks"
    },
    asap: {
      en: "ASAP (30 min - 1 hour)",
      ru: "Как можно скорее (30 мин - 1 час)",
      lv: "ASAP (30 min - 1 stunda)"
    },
    scheduleDelivery: {
      en: "Schedule for later",
      ru: "Запланировать на позже",
      lv: "Ieplānot vēlāk"
    },
    deliveryType: {
      en: "Order Type",
      ru: "Тип заказа",
      lv: "Pasūtījuma veids"
    },
    delivery: {
      en: "Delivery",
      ru: "Доставка",
      lv: "Piegāde"
    },
    pickup: {
      en: "Pickup",
      ru: "Самовывоз",
      lv: "Paņemšana"
    },
    pickupAddress: {
      en: "Pickup at: Liepu iela 9, Liepāja, LV-3401",
      ru: "Pickup at: Liepu iela 9, Liepāja, LV-3401",
      lv: "Saņemt: Liepu iela 9, Liepāja, LV-3401"
    },
    minimumOrderWarning: {
      en: "Minimum order for delivery is €{amount}. Add €{needed} more or choose Pickup.",
      ru: "Минимальный заказ для доставки €{amount}. Добавьте еще €{needed} или выберите Самовывоз.",
      lv: "Minimālais pasūtījums piegādei ir €{amount}. Pievienojiet vēl €{needed} vai izvēlieties Paņemšanu."
    },
    launchDateMessage: {
      en: "Orders will be available from November 15, 2025 at 12:00",
      ru: "Заказы будут доступны с 15 ноября 2025 года в 12:00",
      lv: "Pasūtījumi būs pieejami no 15. novembra 2025. gada plkst. 12:00"
    }
  };

  const timeSlots = generateTimeSlots(schedule, language, restaurantOpen ? translations.asap[language] : null);

  // When closed there is no ASAP option: preselect the first available slot
  useEffect(() => {
    if (!isOpen) return;
    if (!restaurantOpen && !formData.delivery_time && timeSlots.length > 0) {
      setFormData(prev => ({ ...prev, delivery_time: timeSlots[0].value }));
    }
  }, [isOpen, restaurantOpen, timeSlots.length]);

  const validateAddress = async (addressArg, coords) => {
    const address = typeof addressArg === "string" ? addressArg : formData.delivery_address;
    if (!address) {
      return;
    }
    
    setValidatingAddress(true);
    try {
      const response = await axios.post(`${API}/validate-address`, {
        address,
        lat: coords?.lat ?? null,
        lon: coords?.lon ?? null
      });
      setAddressValidation(response.data);
    } catch (error) {
      console.error("Address validation error:", error);
      setAddressValidation(null);
    } finally {
      setValidatingAddress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation - email is now required
    if (!formData.customer_name || !formData.customer_phone || !formData.customer_email) {
      toast.error(translations.fillRequired[language]);
      return;
    }

    // Address is required only for delivery
    if (formData.delivery_type === "delivery" && !formData.delivery_address) {
      toast.error(translations.fillRequired[language]);
      return;
    }

    // Force address validation if delivery type is delivery and not done yet
    if (formData.delivery_type === "delivery" && !addressValidation) {
      setValidatingAddress(true);
      try {
        const response = await axios.post(`${API}/validate-address`, {
          address: formData.delivery_address
        });
        setAddressValidation(response.data);
        
        if (!response.data.valid) {
          toast.error(response.data.message);
          setValidatingAddress(false);
          return;
        }
      } catch (error) {
        console.error("Address validation error:", error);
        toast.error("Could not validate address. Please try again.");
        setValidatingAddress(false);
        return;
      }
      setValidatingAddress(false);
    }

    // Check if address is validated and valid (only for delivery)
    if (formData.delivery_type === "delivery" && addressValidation && !addressValidation.valid) {
      toast.error(addressValidation.message);
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        user_id: user?.id || null, // Link order to logged-in user, null for guest orders
        ...formData,
        items: cart.map(item => ({
          menu_item_id: item.id,
          name: item[`name_${language}`],
          price: item.price,
          quantity: item.quantity
        })),
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        total_amount: totalWithDelivery
      };

      const response = await axios.post(`${API}/orders`, orderData);
      const orderId = response.data.id;
      
      // Update user profile with delivery details (for logged-in users)
      if (user) {
        try {
          await updateProfile({
            full_name: formData.customer_name,
            phone: formData.customer_phone,
            default_delivery_address: formData.delivery_type === "delivery" ? formData.delivery_address : user.default_delivery_address
          });
        } catch (error) {
          console.error("Failed to update user profile:", error);
          // Don't block order completion if profile update fails
        }
      }
      
      // If payment method is card, redirect to Stripe
      if (formData.payment_method === "card") {
        try {
          const checkoutData = {
            origin_url: window.location.origin,
            order_id: orderId,
            amount: totalWithDelivery
          };
          
          const checkoutResponse = await axios.post(`${API}/payments/checkout/session`, checkoutData);
          
          // Redirect to Stripe
          if (checkoutResponse.data.url) {
            window.location.href = checkoutResponse.data.url;
          }
        } catch (error) {
          console.error("Payment error:", error);
          toast.error(translations.paymentError[language]);
          setLoading(false);
        }
      } else {
        // Cash payment - proceed normally
        toast.success(translations.success[language]);
        onOrderSuccess();
        // Encode the order ID to handle the # symbol properly
        navigate(`/order-confirmation/${encodeURIComponent(orderId)}`);
      }
    } catch (error) {
      console.error("Error placing order:", error);
      
      // Check if it's an address validation error
      if (error.response?.status === 400 && error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(translations.error[language]);
      }
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="rz-form max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF7] border border-[#E7E5E4] rounded-none p-7 sm:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.5)]" data-testid="order-modal">
        <DialogHeader className="text-left space-y-2">
          <p className="rz-eyebrow m-0">Roze di Napoli</p>
          <DialogTitle className="font-display text-[2rem] sm:text-[2.3rem] leading-none text-[#1C1917]" data-testid="order-modal-title">{translations.title[language]}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-7 mt-6">
          <div className="space-y-2">
            <Label htmlFor="customer_name" data-testid="name-label">{translations.name[language]} *</Label>
            <Input
              id="customer_name"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              required
              data-testid="name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer_phone" data-testid="phone-label">{translations.phone[language]} *</Label>
            <Input
              id="customer_phone"
              type="tel"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              required
              data-testid="phone-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer_email" data-testid="email-label">{translations.email[language]} *</Label>
            <Input
              id="customer_email"
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              data-testid="email-input"
              required
            />
          </div>

          <div className="space-y-2">
            <Label data-testid="delivery-type-label">{translations.deliveryType[language]}</Label>
            <RadioGroup 
              value={formData.delivery_type} 
              onValueChange={(value) => setFormData({ ...formData, delivery_type: value, delivery_address: value === "pickup" ? "" : formData.delivery_address })} 
              data-testid="delivery-type-radio-group"
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="delivery" id="delivery" data-testid="delivery-type-delivery" />
                <Label htmlFor="delivery" className="font-normal">{translations.delivery[language]}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pickup" id="pickup" data-testid="delivery-type-pickup" />
                <Label htmlFor="pickup" className="font-normal">{translations.pickup[language]}</Label>
              </div>
            </RadioGroup>
          </div>

          {formData.delivery_type === "pickup" ? (
            <div className="rz-note" data-testid="pickup-address-note">
              <p className="font-medium text-[#1C1917] m-0">{translations.pickupAddress[language]}</p>
              <p className="m-0 mt-1">+371 28717331</p>
            </div>
          ) : (
            <div className="space-y-2">
            <Label htmlFor="delivery_address" data-testid="address-label">{translations.address[language]} *</Label>
            <AddressAutocomplete
              value={formData.delivery_address}
              onChange={(value) => {
                setFormData({ ...formData, delivery_address: value });
                setAddressValidation(null);
              }}
              onBlur={() => validateAddress()}
              onValidation={(address, coords) => {
                setFormData((prev) => ({ ...prev, delivery_address: address }));
                validateAddress(address, coords);
              }}
              language={language}
            />
            {validatingAddress && (
              <div className="flex items-center gap-2 text-sm text-[#78716C]">
                <div className="animate-spin rounded-full h-4 w-4 border-b border-[#1C1917]"></div>
                <span>Checking delivery area...</span>
              </div>
            )}
            {addressValidation && (
              <div className={`rz-note ${addressValidation.valid ? 'rz-note--ok' : 'rz-note--warn'}`}>
                {addressValidation.message}
              </div>
            )}
          </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="delivery_time" data-testid="delivery-time-label">{restaurantOpen ? translations.deliveryTime[language] : translations.deliveryTimeRequired[language]}</Label>
            <select
              id="delivery_time"
              value={formData.delivery_time}
              onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
              className="block"
              data-testid="delivery-time-select"
            >
              {timeSlots.map((slot, index) => (
                <option key={index} value={slot.value}>
                  {slot.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label data-testid="payment-label">{translations.payment[language]}</Label>
            <RadioGroup value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })} data-testid="payment-radio-group">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="cash" id="cash" data-testid="payment-cash" />
                <Label htmlFor="cash" className="cursor-pointer">{translations.cash[language]}</Label>
              </div>
              {cardPaymentsEnabled && (
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="card" id="card" data-testid="payment-card" />
                  <Label htmlFor="card" className="cursor-pointer">{translations.card[language]}</Label>
                </div>
              )}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" data-testid="notes-label">{translations.notes[language]}</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              data-testid="notes-input"
            />
          </div>

          <div className="border-t border-[#E7E5E4] pt-6 space-y-3">
            {/* Subtotal */}
            <div className="flex justify-between items-center text-[0.95rem] text-[#78716C]">
              <span>{translations.subtotal[language]}</span>
              <span className="tabular-nums">€{subtotal.toFixed(2)}</span>
            </div>
            
            {/* Delivery Fee (delivery only) */}
            {formData.delivery_type === "delivery" && (
              <div className="flex justify-between items-center text-[0.95rem] text-[#78716C]">
                <span>{translations.deliveryFee[language]}</span>
                {deliveryFee === 0 ? (
                  <span className="text-[#15803d] font-medium">{translations.freeDelivery[language]}</span>
                ) : (
                  <span className="tabular-nums">€{deliveryFee.toFixed(2)}</span>
                )}
              </div>
            )}
            
            {/* Pickup Discount */}
            {pickupDiscount > 0 && (
              <div className="flex justify-between items-center text-[0.95rem] text-[#15803d]">
                <span>{translations.pickupDiscount[language]}</span>
                <span className="font-medium tabular-nums">-€{pickupDiscount.toFixed(2)}</span>
              </div>
            )}
            
            {/* Total */}
            {loyalty && (
              <div className="rz-note flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="loyalty-info">
                <span>
                  {translations.loyaltyProgress[language]}: <strong>{loyaltyInfo.free > 0 ? loyalty.free_every : (loyalty.pizzas_bought + cartPizzas) % loyalty.free_every}/{loyalty.free_every}</strong>
                </span>
                <span className="text-[#78716C] text-sm">
                  {loyaltyInfo.free > 0
                    ? translations.loyaltyFreeNow[language]
                    : `${translations.loyaltyNext[language]} ${loyalty.free_every - ((loyalty.pizzas_bought + cartPizzas) % loyalty.free_every)}`}
                </span>
              </div>
            )}
            {loyaltyInfo.free > 0 && (
              <div className="flex justify-between items-center text-[0.95rem] text-[#15803d]" data-testid="loyalty-discount-row">
                <span>{translations.loyaltyDiscount[language]} ×{loyaltyInfo.free}</span>
                <span className="font-medium tabular-nums">-€{loyaltyInfo.discount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between items-baseline border-t border-[#E7E5E4] pt-4">
              <span className="rz-eyebrow !text-[#1C1917]" data-testid="order-total-label">{translations.total[language]}</span>
              <span className="font-display text-[1.9rem] leading-none text-[#1C1917] tabular-nums" data-testid="order-total-amount">€{totalWithDelivery.toFixed(2)}</span>
            </div>
            
            {/* Minimum order warning for delivery */}
            {isDeliveryBelowMinimum && (
              <div className="rz-note rz-note--warn" data-testid="minimum-order-warning">
                <p className="font-medium m-0">
                  {translations.minimumOrderWarning[language]
                    .replace('{amount}', deliveryConfig.minimum_order_amount.toFixed(2))
                    .replace('{needed}', amountNeeded)}
                </p>
              </div>
            )}
            
            {/* Launch date message - orders not yet available */}
            {ordersNotYetAvailable && (
              <div className="rz-note">
                <p className="font-medium m-0">
                  {translations.launchDateMessage[language]}
                </p>
              </div>
            )}
            
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rz-btn rz-btn--outline flex-1"
                data-testid="cancel-order-button"
              >
                {translations.cancel[language]}
              </button>
              <button
                type="submit"
                disabled={loading || isDeliveryBelowMinimum || ordersNotYetAvailable}
                className="rz-btn flex-1"
                data-testid="place-order-button"
              >
                {loading ? "..." : translations.placeOrder[language]}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderModal;